import { useState, useEffect, useCallback } from "react";
import {
    ITreeMenu,
    useTranslate,
    useLogout,
    useMenu,
    useWarnAboutChange,
    useIsAuthenticated,
    useGetIdentity,
    useApiUrl,
} from "@refinedev/core";
import { Link, useLocation } from "react-router-dom";
import { Layout as AntdLayout, Menu, Grid, MenuProps } from "antd";
import {
    LogoutOutlined,
    UnorderedListOutlined,
} from "@ant-design/icons";
import { antLayoutSider, antLayoutSiderMobile, logoStyle } from "./styles";

import logo from "assets/logo-150.png";
import saintsburg from "assets/saintsburg-transparant.png";
import { extractSimplifiedName } from "utils/functions";
import { ObserverStudents, Student } from "interfaces";
import { axiosInstance } from "@refinedev/simple-rest";

type MenuItem = Required<MenuProps>['items'][number];

export const MainSider = ({ setSiderKey }: { setSiderKey: (key: string) => void }) => {
    const [siderItems, setSiderItems] = useState<MenuItem[]>([]);
    const [collapsed, setCollapsed] = useState<boolean>(false);
    const [openKeys, setOpenKeys] = useState<string[]>([]);

    const apiUrl = useApiUrl();
    const { data: isAuthenticated } = useIsAuthenticated();
    const { warnWhen, setWarnWhen } = useWarnAboutChange();
    const { mutate: mutateLogout } = useLogout();
    const translate = useTranslate();
    const { menuItems, selectedKey, defaultOpenKeys } = useMenu();

    const { data: identity } = useGetIdentity();
    const identity_roles = identity as string[];

    const breakpoint = Grid.useBreakpoint();
    const isMobile = typeof breakpoint.lg === "undefined" ? false : !breakpoint.lg;

    const location = useLocation();
    const currentPath = location.pathname;

    const onOpenChange = useCallback((keys: string[]) => {
        const latestOpenKey = keys.find(key => !openKeys.includes(key));

        if (latestOpenKey) {
            const rootKey = latestOpenKey.split('/').slice(0, -1).join('/');
            const newOpenKeys = keys.filter(key => {
                return key === rootKey || key === latestOpenKey;
            });
            setOpenKeys(newOpenKeys);
        } else {
            setOpenKeys(keys);
        }
    }, [openKeys]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axiosInstance.get(`${apiUrl}/students/overview`, {
                    headers: {
                        "Content-Type": "application/json",
                    }
                });
                setSiderItems((prevItems) => {
                    return prevItems.map((item) => {
                        const newItem = item?.key === "/students"
                            ? { ...item, label: <Link to={"/students"} style={{ color: 'white', textDecoration: 'none' }}>Students</Link>, children: renderStudents(response.data) }
                            : item;
                        return newItem;
                    }
                    )
                });
            } catch (error) {
                console.error(error);
            }
        };
        renderSider();
        if (isAuthenticated?.authenticated) {
            fetchData();
        }
    }, [isAuthenticated, apiUrl]);

    const renderStudents = (inputData: ObserverStudents | undefined) => {
        if (!inputData?.students || inputData.students.length === 0) {
            return [];
        }
        const students = inputData?.students.map((student: Student) => {
            const key = `/students/${student.id}`;
            return {
                key: key,
                label: <Link
                    to={key}
                    style={{
                        color: 'white',
                        textDecoration: 'none',
                    }}
                >{student.name}</Link>,
                children: renderCourses(inputData, student.id)
            };
        });

        return students;
    };

    const renderCourses = (inputData: ObserverStudents | undefined, studentId: number) => {
        const student = inputData?.students.find((student: any) => student.id === studentId);
        if (!student) return [];

        const courseItems = student.courses.map((course: any) => {
            const key = `/students/${studentId}/courses/${course.id}`;
            const isSelected = selectedKey === key;
            return {
                key: key,
                label: <Link
                    to={key}
                    style={{
                        fontWeight: isSelected ? "bold" : "normal",
                    }}
                >{extractSimplifiedName(course.name)}</Link>
            };
        });
        return courseItems;
    };

    const handleEnterCMS = () => {
        setSiderKey("cms");
    }

    const renderTreeView = (tree: ITreeMenu[], selectedKey: string): MenuItem[] => {
        const roles: any = {
            "students": ["observer"],
            "courses": ["admin", "tutor"],
            "papers": ["splitter"],
            "dashboard": ["team-leader"],
            "management": ["admin"],
            "admin": ["admin"],
            "assignments": ["admin", "team-leader", "developer"],
            "tasks": ["admin", "team-leader", "developer"],
            "cms": ["admin"]
        };
        return tree.map((item: ITreeMenu) => {
            if (!isAuthenticated?.authenticated) return null;

            const { identifier, name, list, children, meta } = item;
            const route = list?.toString() ?? name;

            const role_lookup = identifier?.split("/")[0] ?? name;
            if (!identity_roles || !identity_roles.length || !identity_roles.some(role => roles[(role_lookup).toLowerCase()].includes(role))) {
                return null;
            }

            if (name === "CMS") {
                return {
                    key: route,
                    icon: meta?.icon ?? <UnorderedListOutlined />,
                    label: <Link to={route}>{meta?.label}</Link>,
                    onClick: handleEnterCMS
                };
            }

            if (children.length > 0) {
                return {
                    key: route,
                    icon: meta?.icon ?? <UnorderedListOutlined />,
                    label: meta?.label,
                    children: renderTreeView(children, selectedKey)
                }
            }
            const isRoute = !(
                meta?.parent !== undefined &&
                children.length === 0
            );
            return {
                key: route,
                icon: meta?.icon ?? (isRoute && <UnorderedListOutlined />),
                label: route ? <Link to={route}>{meta?.label}</Link> : meta?.label
            };
        }).filter(Boolean) as MenuItem[];
    };

    const handleLogout = () => {
        if (warnWhen) {
            const confirm = window.confirm(
                translate(
                    "warnWhenUnsavedChanges",
                    "Are you sure you want to leave? You have unsaved changes.",
                ),
            );

            if (confirm) {
                setWarnWhen(false);
                mutateLogout();
            }
        } else {
            mutateLogout();
        }
    };

    const renderSider = () => {
        const loginLogout = isAuthenticated?.authenticated ?
            ({
                key: "logout",
                onClick: handleLogout,
                icon: <LogoutOutlined />,
                label: translate("buttons.logout", "Logout")
            }) : ({
                key: "login",
                icon: <LogoutOutlined />,
                label: <Link to="/login">{translate("buttons.login", "Login")}</Link>
            });
        var items = renderTreeView(menuItems, currentPath);
        var extended = items.concat(loginLogout);
        setSiderItems(extended);
        return extended;
    };

    return (
        <AntdLayout.Sider
            collapsible
            collapsedWidth={isMobile ? 0 : 80}
            collapsed={collapsed}
            breakpoint="lg"
            onCollapse={(collapsed: boolean): void => setCollapsed(collapsed)}
            style={isMobile ? antLayoutSiderMobile : antLayoutSider}
        >
            <Link to="/">
                {collapsed ? (
                    <img
                        src={logo}
                        alt="Saintsburg"
                        style={logoStyle}
                    />
                ) : (
                    <img
                        src={saintsburg}
                        alt="Saintsburg"
                        style={logoStyle}
                    />
                )}
            </Link>
            <Menu
                theme="dark"
                defaultOpenKeys={defaultOpenKeys}
                selectedKeys={[currentPath]}
                mode="inline"
                items={siderItems}
                openKeys={openKeys}
                onOpenChange={onOpenChange}
                onClick={() => {
                    if (!breakpoint.lg) {
                        setCollapsed(true);
                    }
                }}
            />
        </AntdLayout.Sider>
    );
};